import { atom, WritableAtom } from 'jotai';
import { SyncStorage } from 'jotai/vanilla/utils/atomWithStorage';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import { isArray, isPlainObject } from 'lodash';
import {
  addMultipleHintsValue,
  addMultipleHintsAsObject,
  addPrimitiveHintValue,
  filterHintsValue,
  PlainObjectHintToRemove,
  removeHintFromObject,
} from './hints.utils';
import { focusAtom } from 'jotai-optics';
import { Entries } from 'src/common/tsHelpers';
import { SetStateAction } from 'react';

const HINTS_LIMIT = 10;

export const createUpdateHintsAtom = <
  TStorageKey extends string,
  THints extends Record<string, any[]>,
>({
  key,
  atomWithStorage,
  storage,
  hintedValueNames,
  initialValues,
}: {
  key: TStorageKey;
  atomWithStorage: WritableAtom<THints, [SetStateAction<THints>], void>;
  storage: SyncStorage<THints>;
  hintedValueNames: (keyof THints)[];
  initialValues: THints;
}) => {
  return atom(
    () => null,
    (
      _,
      set,
      value: Partial<Record<keyof THints, THints[keyof THints] | THints[keyof THints][number]>>,
    ) => {
      const hintsInStorage = storage.getItem(key, initialValues);
      const entries = Object.entries(value) as Entries<THints>;

      const newHints = entries.reduce((currentHints, [name, value]) => {
        if (!hintedValueNames.includes(name)) {
          return currentHints;
        }
        let hints;
        if (isArray(value)) {
          hints = addMultipleHintsValue(value, hintsInStorage?.[name] || [], HINTS_LIMIT);
        } else if (isPlainObject(value)) {
          hints = addMultipleHintsAsObject(
            value,
            hintsInStorage?.[name] as unknown as Record<string, string[]>,
            HINTS_LIMIT,
          );
        } else {
          hints = addPrimitiveHintValue(value, hintsInStorage?.[name] || [], HINTS_LIMIT);
        }

        return {
          ...currentHints,
          [name]: hints,
        };
      }, {} as THints);

      return set(atomWithStorage, newHints);
    },
  );
};

export const createGetterAndRemoverHintAtom = <T extends Record<string, any[]>>(
  hintKey: keyof T,
  initialValues: T,
  focusAtom: WritableAtom<T[keyof T], [SetStateAction<T[keyof T]>], void>,
) =>
  atom(
    (get) => {
      const value = get(focusAtom);
      if (!value) {
        return initialValues[hintKey];
      }
      return value;
    },
    (_, set, valueToRemove) => {
      set(focusAtom, (hints) => {
        if (isPlainObject(hints)) {
          const cleaneadHitns = removeHintFromObject(
            valueToRemove as PlainObjectHintToRemove,
            hints as unknown as Record<string, string[]>,
          );
          return {
            ...hints,
            ...cleaneadHitns,
          } as T[keyof T];
        }

        const filteredHints = hints.filter((hint) =>
          filterHintsValue(valueToRemove as string | { id: string }, hint),
        );
        return filteredHints as T[keyof T];
      });
    },
  );

export const createSyncAtom = <TKey extends string, THints extends Record<string, any[]>>({
  storageKey,
  storage,
  atomWithStorage,
  initialValues,
}: {
  storageKey: TKey;
  atomWithStorage: WritableAtom<THints, [SetStateAction<THints>], void>;
  storage: SyncStorage<THints>;
  initialValues: THints;
}) =>
  atom(
    () => null,
    (_, set) => {
      const hintsInStorage = storage.getItem(storageKey, initialValues);
      if (hintsInStorage) {
        set(atomWithStorage, hintsInStorage);
      }
    },
  );

export const initHints = <T extends Record<string, any>>({
  storageName,
  storageVersion,
  initialValues,
}: {
  storageName: string;
  storageVersion: string;
  initialValues: T;
}) => {
  const STORAGE_KEY = `${storageName}-${storageVersion}` as const;

  const atomLocalStorage = createJSONStorage<T>(() => localStorage);
  const storageAtom = atomWithStorage(STORAGE_KEY, initialValues, atomLocalStorage, {
    getOnInit: true,
  });

  const syncAtom = createSyncAtom<typeof STORAGE_KEY, T>({
    storageKey: STORAGE_KEY,
    storage: atomLocalStorage,
    atomWithStorage: storageAtom,
    initialValues,
  });

  const hintedProperty: (keyof T)[] = Object.keys(initialValues);
  const updateAllAtom = createUpdateHintsAtom({
    key: STORAGE_KEY,
    storage: atomLocalStorage,
    atomWithStorage: storageAtom,
    hintedValueNames: hintedProperty,
    initialValues,
  });

  const gettersAndRemovers = hintedProperty.reduce((gettersAndRemovers, hintProp) => {
    return {
      ...gettersAndRemovers,

      [hintProp]: createGetterAndRemoverHintAtom(
        hintProp,
        initialValues,
        focusAtom(storageAtom, (optic) => optic.prop(hintProp)),
      ),
    };
  }, {} as Record<keyof T, WritableAtom<T[keyof T], [valueToRemove: any], void>>);

  return {
    storageAtom,
    syncAtom,
    updateAllAtom,
    gettersAndRemovers,
  };
};
